import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { WorkOrderId } from "../lib/workIds";
import {
  fetchProcedureExecution,
  newerProcedureExecution,
  type ProcedureExecution,
  type ProcedureExecutionScope,
  ProcedureItemConflictError,
  procedureExecutionQueryKey,
  procedureItemRevision,
  saveProcedureItem,
} from "../queries/useProcedureExecution";

/** Photos being added to, or removed from, one PHOTO item. */
export type ProcedurePhotoChange = {
  itemId: string;
  kind: "add" | "remove";
  fileIds: string[];
};

function savedFileIds(execution: ProcedureExecution, itemId: string) {
  const value = execution.responses.find(
    (response) => response.itemId === itemId,
  )?.value;
  return value?.type === "PHOTO" ? value.fileIds : [];
}

/** The item's photo list after applying the change to the server's list. */
export function mergePhotoChange(
  serverFileIds: string[],
  change: ProcedurePhotoChange,
) {
  const changed = new Set(change.fileIds);
  if (change.kind === "remove")
    return serverFileIds.filter((fileId) => !changed.has(fileId));
  return [
    ...serverFileIds,
    ...change.fileIds.filter((fileId) => !serverFileIds.includes(fileId)),
  ];
}

function savePhotoItem(
  scope: ProcedureExecutionScope,
  executionId: string,
  base: { fileIds: string[]; version: number },
  change: ProcedurePhotoChange,
) {
  // Only this PHOTO item is sent, so answers to other items on the page are
  // neither saved nor overwritten by a photo save.
  return saveProcedureItem(scope, change.itemId, {
    value: {
      type: "PHOTO",
      fileIds: mergePhotoChange(base.fileIds, change),
    },
    expectedItemVersion: base.version,
    idempotencyKey: `${executionId}:photos:${crypto.randomUUID()}`,
  });
}

function successMessage(change: ProcedurePhotoChange) {
  if (change.kind === "remove") return "Photo removed.";
  return change.fileIds.length === 1 ? "Photo added." : "Photos added.";
}

/**
 * Saves a photo change to one PHOTO item as soon as it happens, quoting the
 * item's own concurrency token. If someone else changed the same item first
 * (409), the change is reapplied once to the photo list they saved, so neither
 * side's photos are lost.
 */
export function useSaveProcedurePhotosMutation(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  const queryKey = procedureExecutionQueryKey(
    selectedCustomer?.id,
    selectedFacility?.id,
    workOrderId,
  );

  return useMutation({
    mutationFn: async (change: ProcedurePhotoChange) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const scope = {
        customerId: selectedCustomer.id,
        facilityId: selectedFacility.id,
        workOrderId,
      };
      const cached = queryClient.getQueryData<ProcedureExecution>(queryKey);
      const latest = cached ?? (await fetchProcedureExecution(scope));
      try {
        return await savePhotoItem(
          scope,
          latest.executionId,
          {
            fileIds: savedFileIds(latest, change.itemId),
            version: procedureItemRevision(latest, change.itemId),
          },
          change,
        );
      } catch (error) {
        if (!(error instanceof ProcedureItemConflictError)) throw error;
        const theirs = error.current;
        return savePhotoItem(
          scope,
          latest.executionId,
          {
            fileIds: theirs.value?.type === "PHOTO" ? theirs.value.fileIds : [],
            version: theirs.version,
          },
          change,
        );
      }
    },
    onSuccess: (data, change) => {
      queryClient.setQueryData<ProcedureExecution>(queryKey, (cached) =>
        newerProcedureExecution(cached, data),
      );
      // Every stored change is a Work Order history event.
      void queryClient.invalidateQueries({ queryKey: ["work-order-detail"] });
      toast.add(successMessage(change), { icon: true });
    },
    onError: (error: Error) => toast.error(error.message, { icon: true }),
  });
}
